import React, { useEffect, useState } from "react";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";

import API_BASE from "../config";
import UploadService from "../services/upload.service";
import Spinner from "./Spinner";

const Upload = () => {
  const { t } = useTranslation();
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState("");
  const [progress, setProgress] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null); // { url, image_url, filename }
  const [gallery, setGallery] = useState([]);
  const [galleryTotal, setGalleryTotal] = useState(0);
  const [galleryUnassigned, setGalleryUnassigned] = useState(0);
  const [galleryLoading, setGalleryLoading] = useState(false);
  const [showUnassignedOnly, setShowUnassignedOnly] = useState(false);
  const [search, setSearch] = useState("");
  const [deleting, setDeleting] = useState("");

  const resolveMsg = (err) =>
    (err.response && err.response.data && err.response.data.message) ||
    err.message ||
    t("upload.failed");

  const handleSelect = (e) => {
    const f = e.target.files && e.target.files[0];
    setResult(null);
    setProgress(0);
    if (!f) {
      setFile(null);
      setPreview("");
      return;
    }
    if (!f.type.startsWith("image/")) {
      toast.error(t("upload.selectImage"));
      setFile(null);
      setPreview("");
      return;
    }
    setFile(f);
    setPreview(URL.createObjectURL(f));
  };

  const loadGallery = () => {
    setGalleryLoading(true);
    UploadService.list()
      .then((res) => {
        setGallery(res.data.items || []);
        setGalleryTotal(res.data.total || 0);
        setGalleryUnassigned(res.data.unassigned || 0);
      })
      .catch((err) => toast.error(resolveMsg(err)))
      .finally(() => setGalleryLoading(false));
  };

  useEffect(() => {
    loadGallery();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleUpload = (e) => {
    e.preventDefault();
    setResult(null);
    if (!file) {
      toast.error(t("upload.selectFirst"));
      return;
    }
    setUploading(true);
    setProgress(0);
    UploadService.uploadImage(file, setProgress)
      .then((res) => {
        setResult(res.data);
        loadGallery(); // refresh list with the new file
      })
      .catch((err) => toast.error(resolveMsg(err)))
      .finally(() => setUploading(false));
  };

  const reset = () => {
    setFile(null);
    setPreview("");
    setProgress(0);
    setResult(null);
  };

  const publicUrl = result ? API_BASE + result.url : "";

  const copy = (text) => {
    if (navigator.clipboard) navigator.clipboard.writeText(text);
  };

  const handleDeleteImage = (g) => {
    if (g.assigned) return; // safety: never delete assigned files
    if (!window.confirm(t("upload.confirmDelete", { name: g.filename }))) return;
    setDeleting(g.filename);
    UploadService.remove(g.filename)
      .then(() => loadGallery())
      .catch((err) => toast.error(resolveMsg(err)))
      .finally(() => setDeleting(""));
  };

  const q = search.trim().toLowerCase();
  const visibleGallery = gallery.filter((g) => {
    if (showUnassignedOnly && g.assigned) return false;
    if (!q) return true;
    const haystack = [
      g.filename,
      g.url,
      g.image_url,
      ...(g.usedBy ? g.usedBy.map((u) => u.name) : []),
    ]
      .filter(Boolean)
      .join(" ")
      .toLowerCase();
    return haystack.includes(q);
  });

  return (
    <div className="container mt-4" style={{ maxWidth: 960 }}>
      <h3>{t("upload.title")}</h3>
      <p className="text-muted">
        {t("upload.storedNote")}
      </p>


      <form onSubmit={handleUpload} className="card card-body mb-4">
        <div className="mb-3">
          <label className="form-label">{t("upload.fileLabel")}</label>
          <input
            type="file"
            className="form-control"
            accept="image/*"
            onChange={handleSelect}
          />
          <div className="form-text">{t("upload.fileHint")}</div>
        </div>

        {preview && (
          <div className="mb-3">
            <img
              src={preview}
              alt="preview"
              style={{ maxHeight: 180, borderRadius: 6, border: "1px solid #ddd" }}
            />
          </div>
        )}

        {uploading && (
          <div className="progress mb-3">
            <div
              className="progress-bar"
              role="progressbar"
              style={{ width: `${progress}%` }}
              aria-valuenow={progress}
              aria-valuemin="0"
              aria-valuemax="100"
            >
              {progress}%
            </div>
          </div>
        )}

        <div className="d-flex gap-2">
          <button type="submit" className="btn btn-primary" disabled={uploading || !file}>
            {uploading ? t("upload.uploading") : t("upload.uploadBtn")}
          </button>
          <button type="button" className="btn btn-secondary" onClick={reset}>
            {t("common.clear")}
          </button>
        </div>
      </form>

      {result && (
        <div className="alert alert-success">
          <h5 className="mb-2">{t("upload.success")}</h5>
          <img
            src={publicUrl}
            alt={result.filename}
            style={{ maxHeight: 160, borderRadius: 6, border: "1px solid #ddd" }}
            className="mb-3 d-block"
          />
          <div className="mb-2">
            <strong>{t("upload.publicUrl")}</strong>
            <div className="input-group mt-1">
              <input className="form-control" readOnly value={publicUrl} />
              <button
                type="button"
                className="btn btn-outline-secondary"
                onClick={() => copy(publicUrl)}
              >
                {t("upload.copy")}
              </button>
            </div>
          </div>
          <div>
            <strong>{t("upload.storeInProduct")}</strong>
            <div className="input-group mt-1">
              <input className="form-control" readOnly value={result.image_url} />
              <button
                type="button"
                className="btn btn-outline-secondary"
                onClick={() => copy(result.image_url)}
              >
                {t("upload.copy")}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* GALLERY: all uploaded files with preview + assignment status */}
      <div className="mt-4">
        <div className="d-flex flex-wrap align-items-center justify-content-between mb-2 gap-2">
          <h4 className="mb-0">
            {t("upload.galleryTitle")}{" "}
            <small className="text-muted">
              {t("upload.galleryCount", { total: galleryTotal, unassigned: galleryUnassigned })}
            </small>
          </h4>
          <div className="d-flex align-items-center gap-3">
            <div className="input-group input-group-sm" style={{ width: 240 }}>
              <input
                type="text"
                className="form-control"
                placeholder={t("upload.searchPlaceholder")}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
              {search && (
                <button
                  type="button"
                  className="btn btn-outline-secondary"
                  onClick={() => setSearch("")}
                  title={t("common.clear")}
                >
                  &times;
                </button>
              )}
            </div>
            <div className="form-check mb-0">
              <input
                className="form-check-input"
                type="checkbox"
                id="unassignedOnly"
                checked={showUnassignedOnly}
                onChange={(e) => setShowUnassignedOnly(e.target.checked)}
              />
              <label className="form-check-label" htmlFor="unassignedOnly">
                {t("upload.unassignedOnly")}
              </label>
            </div>
            <button type="button" className="btn btn-sm btn-outline-secondary" onClick={loadGallery}>
              {t("upload.refresh")}
            </button>
          </div>
        </div>

        {galleryLoading ? (
          <Spinner />
        ) : visibleGallery.length === 0 ? (
          <p className="text-muted">
            {q || showUnassignedOnly ? t("upload.galleryNoMatch") : t("upload.galleryEmpty")}
          </p>
        ) : (
          <div className="row">
            {visibleGallery.map((g) => (
              <div className="col-6 col-md-4 col-lg-3 mb-4" key={g.filename}>
                <div className={"card h-100 " + (g.assigned ? "" : "border-warning")}>
                  <img
                    src={API_BASE + g.url}
                    alt={g.filename}
                    className="card-img-top"
                    style={{ height: 140, objectFit: "cover", background: "#f5f5f5" }}
                    onError={(e) => { e.target.style.visibility = "hidden"; }}
                  />
                  <div className="card-body p-2">
                    {g.assigned ? (
                      <span className="badge bg-success mb-1">{t("upload.assigned")}</span>
                    ) : (
                      <span className="badge bg-warning text-dark mb-1">{t("upload.unassigned")}</span>
                    )}
                    <div className="small text-truncate" title={g.filename}>{g.filename}</div>
                    {g.assigned && (
                      <div className="small text-muted text-truncate" title={g.usedBy.map((u) => u.name).join(", ")}>
                        {g.usedBy.map((u) => u.name).join(", ")}
                      </div>
                    )}
                    <div className="small text-muted mt-1">{t("upload.publicUrl")}</div>
                    <div className="input-group input-group-sm">
                      <input className="form-control" readOnly value={API_BASE + g.url} />
                      <button
                        type="button"
                        className="btn btn-outline-secondary"
                        onClick={() => copy(API_BASE + g.url)}
                        title={t("upload.copy")}
                      >
                        {t("upload.copy")}
                      </button>
                    </div>
                    <div className="small text-muted mt-1">{t("upload.storeInProduct")}</div>
                    <div className="input-group input-group-sm">
                      <input className="form-control" readOnly value={g.image_url} />
                      <button
                        type="button"
                        className="btn btn-outline-secondary"
                        onClick={() => copy(g.image_url)}
                      >
                        {t("upload.copy")}
                      </button>
                    </div>
                    {!g.assigned && (
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-danger w-100 mt-2"
                        onClick={() => handleDeleteImage(g)}
                        disabled={deleting === g.filename}
                      >
                        {deleting === g.filename ? t("upload.deleting") : t("upload.delete")}
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default Upload;
